import { useEffect, useMemo, useState } from "react";
import type { FormField } from "../components/AdminDataModal";
import { useMessageModal } from "../contexts/MessageModalContext";
import { useFormat } from "../../context/FormatContext";
import AdminListToolkit from "../components/listing/AdminListToolkit";
import AdminDataModal from "../components/AdminDataModal";
import "../styles/adminPayments.css";

interface PaymentRow {
  id: number;
  orderId: number;
  orderNumber: string;
  provider: string;
  paymentId: string | null;
  exchangeNumber: string | null;
  merchantReferenceNumber: string | null;
  fpxTransactionId: string | null;
  paymentStatus: string | null;
  fpxStatus: string | null;
  fpxDebitAuthCode: string | null;
  transactionAmount: number | null;
  transactionAmountReceived: number | null;
  currency: string | null;
  checksumValid: number;
  rawPayload: string | null;
  createdAt: string;
  updatedAt: string;
  order?: {
    buyerName?: string;
    buyerEmail?: string;
    paymentMethod?: string;
    orderStatus?: string;
  };
}

export default function AdminPayments() {
  const BASE = import.meta.env.VITE_LYRAZONE_BASE_URL;
  const { showModal } = useMessageModal();
  const { formatDateToDMY, formatCurrency } = useFormat();

  const [data, setData] = useState<PaymentRow[]>([]);
  const [selectedRow, setSelectedRow] = useState<PaymentRow | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"view" | "edit">("view");
  const [activeRow, setActiveRow] = useState<PaymentRow | null>(null);
  const [expandedRows, setExpandedRows] = useState<Record<number, boolean>>({});

  // const formatCurrency = (value?: number | null, currency?: string | null) => {
  //   const amount = Number(value || 0);
  //   return new Intl.NumberFormat("en-MY", {
  //     style: "currency",
  //     currency: currency || "MYR",
  //   }).format(amount);
  // };

  const toLabel = (value: string | number | null | undefined) =>
    String(value ?? "")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (c) => c.toUpperCase());

  const paymentFields: FormField[] = useMemo(
    () => [
      { key: "id", label: "Payment ID (Row)", colSpan: 1 },
      { key: "orderNumber", label: "Order #", colSpan: 1 },
      { key: "provider", label: "Provider", colSpan: 1 },
      { key: "currency", label: "Currency", colSpan: 1 },

      { key: "paymentId", label: "Gateway Payment ID", colSpan: 1 },
      { key: "exchangeNumber", label: "Exchange Number", colSpan: 1 },
      {
        key: "merchantReferenceNumber",
        label: "Merchant Reference Number",
        colSpan: 1,
      },
      { key: "fpxTransactionId", label: "FPX Transaction ID", colSpan: 1 },

      {
        key: "paymentStatus",
        label: "Payment Status",
        type: "select",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Success", value: "success" },
          { label: "Failed", value: "failed" },
          { label: "True", value: "true" },
          { label: "False", value: "false" },
        ],
        colSpan: 1,
      },
      {
        key: "fpxStatus",
        label: "FPX Status",
        type: "select",
        options: [
          { label: "Pending", value: "pending" },
          { label: "Success", value: "success" },
          { label: "Failed", value: "failed" },
          { label: "True", value: "true" },
          { label: "False", value: "false" },
        ],
        colSpan: 1,
      },
      { key: "fpxDebitAuthCode", label: "FPX Debit Auth Code", colSpan: 1 },
      {
        key: "checksumValid",
        label: "Checksum Valid",
        type: "select",
        options: [
          { label: "No", value: "0" },
          { label: "Yes", value: "1" },
        ],
        colSpan: 1,
      },

      { key: "transactionAmount", label: "Transaction Amount", colSpan: 1 },
      {
        key: "transactionAmountReceived",
        label: "Amount Received",
        colSpan: 1,
      },
      { key: "createdAt", label: "Created At", colSpan: 1 },
      { key: "updatedAt", label: "Updated At", colSpan: 1 },

      { key: "buyerName", label: "Buyer Name", colSpan: 1 },
      { key: "buyerEmail", label: "Buyer Email", colSpan: 1 },
      { key: "paymentMethod", label: "Order Payment Method", colSpan: 1 },
      { key: "orderStatus", label: "Order Status", colSpan: 1 },

      {
        key: "rawPayload",
        label: "Raw Payload",
        type: "textarea",
        colSpan: 2,
      },
    ],
    [],
  );

  const fetchPayments = async () => {
    try {
      const res = await fetch(`${BASE}/admin/payments`, {
        credentials: "include",
      });
      const result = await res.json();

      if (result?.success) {
        setData(result.data || []);
      } else {
        setData([]);
      }
    } catch (error) {
      console.error("fetchPayments error:", error);
      setData([]);
    }
  };

  useEffect(() => {
    fetchPayments();
  }, []);

  const toggleExpanded = (id: number) => {
    setExpandedRows((prev) => ({
      ...prev,
      [id]: !prev[id],
    }));
  };

  const buildModalRowData = (row: PaymentRow) => ({
    ...row,
    transactionAmount: formatCurrency(row.transactionAmount),
    transactionAmountReceived: formatCurrency(row.transactionAmountReceived),
    createdAt: row.createdAt ? new Date(row.createdAt).toLocaleString() : "",
    updatedAt: row.updatedAt ? new Date(row.updatedAt).toLocaleString() : "",
    buyerName: row.order?.buyerName || "",
    buyerEmail: row.order?.buyerEmail || "",
    paymentMethod: row.order?.paymentMethod || "",
    orderStatus: row.order?.orderStatus || "",
    checksumValid: String(row.checksumValid ?? 0),
    rawPayload:
      typeof row.rawPayload === "string"
        ? row.rawPayload
        : JSON.stringify(row.rawPayload ?? "", null, 2),
  });

  const handleView = (row: PaymentRow) => {
    setModalMode("view");
    setActiveRow(row);
    setModalOpen(true);
  };

  const handleEdit = (row: PaymentRow) => {
    setModalMode("edit");
    setActiveRow(row);
    setModalOpen(true);
  };

  const handleSubmit = async (formData: any) => {
    if (!activeRow) return;

    try {
      const res = await fetch(`${BASE}/admin/payments/${activeRow.id}`, {
        method: "PATCH",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          paymentStatus: formData.paymentStatus,
          fpxStatus: formData.fpxStatus,
          fpxDebitAuthCode: formData.fpxDebitAuthCode,
          checksumValid: Number(formData.checksumValid),
        }),
      });

      const result = await res.json();

      if (result.success) {
        await fetchPayments();
        setModalOpen(false);
        setActiveRow(null);

        showModal({
          type: "success",
          title: "Success",
          message: "Payment updated successfully.",
        });
      } else {
        showModal({
          type: "error",
          title: "Error",
          message: result.message || "Unable to update payment.",
        });
      }
    } catch (error) {
      console.error("handleSubmit error:", error);
      showModal({
        type: "error",
        title: "Error",
        message: "Unable to update payment.",
      });
    }
  };

  const renderStatusBadge = (status: string | null) => {
    const normalized = String(status || "").toLowerCase();
    const className =
      normalized === "success" || normalized === "true"
        ? "badge-success"
        : normalized === "failed" || normalized === "false"
          ? "badge-danger"
          : normalized === "pending"
            ? "badge-warning"
            : "badge-secondary";

    return <span className={className}>{toLabel(status || "-")}</span>;
  };

  const renderChecksumBadge = (value: number) => {
    return value === 1 ? (
      <span className="badge-success">Valid</span>
    ) : (
      <span className="badge-danger">Invalid</span>
    );
  };

  return (
    <div>
      <AdminListToolkit
        title="Payments"
        columns={[]}
        data={data}
        selectedRow={selectedRow}
        onView={(row) => handleView(row as PaymentRow)}
        onEdit={(row) => handleEdit(row as PaymentRow)}
        enableExport
        exportFileName="payments"
      >
        {(filteredData: PaymentRow[]) => (
          <div className="admin-payments-table-wrap">
            <table className="admin-payments-table">
              <thead>
                <tr>
                  <th style={{ width: "56px" }}></th>
                  <th>Order #</th>
                  <th>Provider</th>
                  <th>Payment Status</th>
                  <th>FPX Status</th>
                  <th>Amount</th>
                  <th>Received</th>
                  <th>Checksum</th>
                  <th>Created</th>
                </tr>
              </thead>

              <tbody>
                {filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="admin-payments-empty">
                      No data available
                    </td>
                  </tr>
                ) : (
                  filteredData.map((row) => {
                    const expanded = !!expandedRows[row.id];

                    return (
                      <>
                        <tr
                          key={row.id}
                          className={`admin-payments-parent-row ${
                            selectedRow?.id === row.id ? "selected" : ""
                          }`}
                          onClick={() => setSelectedRow(row)}
                        >
                          <td>
                            <button
                              type="button"
                              className="admin-payments-expand-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                toggleExpanded(row.id);
                              }}
                            >
                              {expanded ? "-" : "+"}
                            </button>
                          </td>

                          <td>{row.orderNumber}</td>
                          <td>{row.provider}</td>
                          <td>{renderStatusBadge(row.paymentStatus)}</td>
                          <td>{renderStatusBadge(row.fpxStatus)}</td>
                          <td>{formatCurrency(row.transactionAmount)}</td>
                          <td>
                            {formatCurrency(row.transactionAmountReceived)}
                          </td>
                          <td>{renderChecksumBadge(row.checksumValid)}</td>
                          <td>
                            {row.createdAt
                              ? formatDateToDMY(row.createdAt)
                              : ""}
                          </td>
                        </tr>

                        {expanded && (
                          <tr className="admin-payments-children-row">
                            <td colSpan={9}>
                              <div className="admin-payments-children-box">
                                <div className="admin-payments-summary">
                                  <h4>Payment Summary</h4>
                                  <p>
                                    <strong>Gateway Payment ID:</strong>{" "}
                                    {row.paymentId || "-"}
                                  </p>
                                  <p>
                                    <strong>Exchange Number:</strong>{" "}
                                    {row.exchangeNumber || "-"}
                                  </p>
                                  <p>
                                    <strong>Merchant Ref No:</strong>{" "}
                                    {row.merchantReferenceNumber || "-"}
                                  </p>
                                  <p>
                                    <strong>FPX Transaction ID:</strong>{" "}
                                    {row.fpxTransactionId || "-"}
                                  </p>
                                  <p>
                                    <strong>FPX Debit Auth Code:</strong>{" "}
                                    {row.fpxDebitAuthCode || "-"}
                                  </p>
                                  <p>
                                    <strong>Currency:</strong>{" "}
                                    {row.currency || "-"}
                                  </p>
                                </div>

                                <div className="admin-payments-order-box">
                                  <h4>Order Summary</h4>
                                  <p>
                                    <strong>Buyer Name:</strong>{" "}
                                    {row.order?.buyerName || "-"}
                                  </p>
                                  <p>
                                    <strong>Buyer Email:</strong>{" "}
                                    {row.order?.buyerEmail || "-"}
                                  </p>
                                  <p>
                                    <strong>Payment Method:</strong>{" "}
                                    {row.order?.paymentMethod || "-"}
                                  </p>
                                  <p>
                                    <strong>Order Status:</strong>{" "}
                                    {toLabel(row.order?.orderStatus || "-")}
                                  </p>
                                </div>

                                <div className="admin-payments-payload-box">
                                  <h4>Raw Payload</h4>
                                  <pre>
                                    {typeof row.rawPayload === "string"
                                      ? row.rawPayload
                                      : JSON.stringify(
                                          row.rawPayload ?? {},
                                          null,
                                          2,
                                        )}
                                  </pre>
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        )}
      </AdminListToolkit>

      <AdminDataModal
        isOpen={modalOpen}
        mode={modalMode}
        title="Payment"
        fields={paymentFields}
        rowData={activeRow ? buildModalRowData(activeRow) : null}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSubmit}
        size="xl"
      />
    </div>
  );
}
